import type {DateOption} from '@freckle/educator-entities/ts/common/helpers/date-options'
import type {LangT} from '@freckle/educator-entities/ts/common/helpers/languages'
import {PATHS} from '@freckle/educator-entities/ts/common/helpers/paths'
import {toFragment} from '@freckle/educator-entities/ts/common/helpers/routers/query-params'
import type {SubjectT} from '@freckle/educator-entities/ts/common/helpers/subjects'
import type {RlDomainIdT} from '@freckle/educator-entities/ts/common/types/rl-domain-id'
import type {RlSkillUspIdT} from '@freckle/educator-entities/ts/common/types/rl-skill-usp-id'
import type {RlStandardIdT} from '@freckle/educator-entities/ts/common/types/rl-standard-id'
import type {MathAssignmentOptionT} from '@freckle/educator-entities/ts/math/common/models/math-assignment'
import {MathAssignmentTypes} from '@freckle/educator-entities/ts/math/common/models/math-assignment'
import type {MathAssignmentTypeT} from '@freckle/educator-entities/ts/math/common/models/math-assignment'
import type {SupportSettingT} from '@freckle/classroom/ts/common/components/add-assignment/targeted-assignments/support-setting'
import type {ElaAssignmentTypeT} from '@freckle/classroom/ts/common/logic/assign-article'
import type {ElaAssignmentOptionT} from '@freckle/classroom/ts/ela/common/models/ela-product'

export type TargetedPracticeReportTabT = 'students' | 'questions'

export type RosterStudentEditPageTab = 'details' | 'dashboard' | 'support-settings'

export type FactPracticeReportTabNameT = 'activity' | 'basic-operations' | 'integers-and-fractions'

type ElaAssignmentReport = 'assessment' | 'reading' | 'writing'

export type AssignTargetedMathSubtypeT =
  'practice' | 'prerequisite' | 'review' | 'external_adaptive'

export type AssignWordStudyPracticeParamsT =
  | {tag: 'elaStandardId'; elaStandardId: RlStandardIdT; studentIds?: Array<number>}
  | {tag: 'elaSkillUspId'; elaSkillUspId: RlSkillUspIdT; studentIds?: Array<number>}

const mathRoutes = {
  overview: (params?: {courseId?: number; tab?: string; grade?: number; skillSetId?: number}) =>
    toFragment('/math/overview', {tab: 'standards', ...(params ?? {})}),
  factFluencyOverview: () => '/math/goals/fact-fluency-overview',
  targetedSessionReport: (assignmentSessionId: number) =>
    `/math/targeted-session/${assignmentSessionId}/report`,
  chromelessTargetedSessionReport: (assignmentSessionId: number) =>
    `/chromeless/math/targeted-session/${assignmentSessionId}/report`,
  focusSkillsSessionReport: (assignmentSessionId: number) =>
    `/math/focus-skills-practice-session/${assignmentSessionId}/report`,
  adaptiveSessionReport: (assignmentSessionId: number) =>
    `/math/adaptive-session/${assignmentSessionId}/report`,
  assessmentSessionReport: (assignmentSessionId: number) =>
    `/math/assessment-session/${assignmentSessionId}/report`,
  standardsStudentReport: (
    studentId: number,
    params: {
      standardId?: string
      domainId?: string
      from?: number
      to?: number
      'date-option'?: DateOption
      practiceType?: MathAssignmentOptionT
    }
  ) =>
    toFragment(`/math/standards/students/${studentId}/report`, {
      ...params
    }),
  assignmentSessionReport: (product: string, assignmentSessionId: number, courseId?: number) =>
    toFragment(`/math/session/${product}/${assignmentSessionId}/report`, {
      courseId
    }),
  standard: ({courseId, standardId}: {courseId?: number; standardId: string}) =>
    toFragment(`/standards/${standardId}`, {courseId}),
  previewPrintAssignment: (courseId: number, product: string, sessionId: number) =>
    toFragment(`/math/print/${product}/${sessionId}`, {courseId}),
  assignments: (courseId?: number) => toFragment(`/math/assignments`, {courseId}),
  sessionHistory: ({
    courseId,
    assignmentType
  }: {
    courseId?: number
    assignmentType: MathAssignmentTypeT
  }) =>
    toFragment(`/session-history/${MathAssignmentTypes.forDisplay(assignmentType)}`, {
      courseId
    }),
  sessionHistoryAssignment: ({
    courseId,
    assignmentType,
    assignmentId
  }: {
    courseId?: number
    assignmentType: MathAssignmentTypeT
    assignmentId: number
  }) =>
    toFragment(
      `/session-history/${MathAssignmentTypes.forDisplay(assignmentType)}/${assignmentId}`,
      {courseId}
    ),
  sessionHistoryAssignmentSession: ({
    courseId,
    assignmentType,
    assignmentId,
    sessionId
  }: {
    courseId?: number
    assignmentType: MathAssignmentTypeT
    assignmentId: number
    sessionId: number
  }) =>
    toFragment(
      `/session-history/${MathAssignmentTypes.forDisplay(assignmentType)}/${assignmentId}/session/${sessionId}`,
      {courseId}
    ),
  sessionHistoryAssessmentSession: ({
    courseId,
    sessionId
  }: {
    courseId?: number
    sessionId: number
  }) => toFragment(`/session-history/math-assessment-session/${sessionId}`, {courseId}),
  reportCardSummaries: (courseId?: number) => toFragment(`/report-card-summaries`, {courseId}),
  reportCard: ({
    courseId,
    student,
    queryParams
  }: {
    courseId?: number
    student?: number
    queryParams?: object
  }) => {
    return toFragment(`/math-report-card`, {
      courseId,
      student,
      ...queryParams
    })
  },
  printReportCards: (courseId?: number) => toFragment(`/print-math-report-cards`, {courseId}),
  factPracticeReports: (params?: {
    courseId?: number
    tabName?: FactPracticeReportTabNameT
    initialStudentId?: number
  }) =>
    toFragment(`/fact-practice-reports`, {
      courseId: params?.courseId,
      tabName: params?.tabName,
      'student-id': params?.initialStudentId
    }),
  factPracticeReport: ({
    courseId,
    studentId,
    operation
  }: {
    courseId?: number
    studentId: number
    operation: string
  }) =>
    toFragment(`/fact-practice-report/students/${studentId}/operations/${operation}`, {
      courseId
    }),
  standardsReport: (courseId?: number) => toFragment(`/math-standards-report`, {courseId}),
  levelsReport: (courseId?: number) => toFragment(`/math-diagnostic-report`, {courseId}),
  adaptiveReport: (courseId?: number) => toFragment(`/math-adaptive-report`, {courseId}),
  focusSkillsReport: (courseId?: number) => toFragment(`/math-focus-skills-report`, {courseId}),
  skillsProgressReport: (courseId?: number) =>
    toFragment(`/math-skills-progress-report`, {courseId}),
  assessmentGrowthReport: (courseId?: number) => toFragment(`/math-assessment-growth`, {courseId}),
  assessmentGrowthDetailed: (courseId: number, assignmentIds: Array<number>) =>
    toFragment(`/math-assessment-growth-detailed`, {assignmentIds, courseId}),
  classGroupingReport: (courseId?: number) => toFragment(`/class-grouping-report`, {courseId}),
  targetedPracticeReport: ({
    courseId,
    assignmentId,
    tab
  }: {
    courseId?: number
    assignmentId: number
    tab?: TargetedPracticeReportTabT
  }) =>
    toFragment(`/math/reports/targeted-practice/${assignmentId}`, {
      courseId,
      tab
    }),
  chromelessTargetedPracticeReport: (assignmentId: number) =>
    `/chromeless/math/reports/targeted-practice/${assignmentId}`,
  assignIblLesson: ({
    courseId,
    lessonId,
    studentIds,
    days
  }: {
    courseId?: number
    lessonId: number
    studentIds?: Array<number>
    days?: Array<number>
  }) =>
    toFragment(`/lessons/${lessonId}/assignments`, {
      courseId,
      studentIds,
      days
    }),
  dokAssignmentReport: ({courseId, assignmentId}: {courseId?: number; assignmentId: number}) =>
    toFragment(`/math/reports/dok-assignment/${assignmentId}`, {courseId}),
  chromelessDokAssignmentReport: (assignmentId: number, courseId?: number) =>
    toFragment(`/chromeless/math/reports/dok-assignment/${assignmentId}`, {courseId}),
  chromelessMathSessionReport: (product: string, assignmentSessionId: number, courseId?: number) =>
    toFragment(`/chromeless/math/session/${product}/${assignmentSessionId}/report`, {
      courseId
    }),
  assignTargetedDepthOfKnowledgePractice: (params?: {
    courseId?: number
    students?: Array<number>
    domainId?: RlDomainIdT
    standardId?: RlStandardIdT
  }) => toFragment(`/assignments/targeted-depth-of-knowledge-practice`, params),
  assignFocusSkillsPractice: (params?: {courseId?: number; studentIds?: Array<number>}) =>
    toFragment(`/assignments/math-focus-skills-practice`, params),

  focusSkillsPracticeReport: (courseId: number, assignmentId: number) =>
    toFragment(`/reports/math-focus-skills-practice/${assignmentId}`, {
      courseId
    }),

  assignNumberFacts: (params?: {courseId?: number; students?: Array<number>}) =>
    toFragment(`/assignments/number-facts`, params),

  assignFactPractice: (params?: {
    courseId?: number
    students?: Array<number>
    operations?: Array<string>
  }) => toFragment(`/assignments/fact-practice`, params),
  lessonsLesson: ({courseId, lessonId}: {courseId?: number; lessonId: number}) =>
    toFragment(`/lessons/${lessonId}`, {courseId}),
  assignLesson: ({
    courseId,
    lessonId,
    students,
    days
  }: {
    courseId?: number
    lessonId: number
    students?: Array<number>
    days?: Array<number>
  }) =>
    toFragment(`/lessons/${lessonId}/assignments`, {
      courseId,
      students,
      days
    }),
  assignAdaptive: (params?: {
    courseId?: number
    students?: Array<number>
    domainId?: RlDomainIdT | null | undefined
  }) => toFragment(`/assignments/adaptive`, params),
  assignAssessments: (params?: {
    courseId?: number
    students?: Array<number>
    grade?: number
    domainIds?: Array<RlDomainIdT>
    standardIds?: Array<RlStandardIdT>
  }) => toFragment(`/assignments/assessments`, params),
  assessmentReport: ({courseId, assessmentId}: {courseId?: number; assessmentId: number}) =>
    toFragment(`/assessment/${assessmentId}/report`, {courseId}),
  assignTargeted: ({
    courseId,
    numQuestions,
    students,
    params
  }: {
    courseId?: number
    numQuestions?: number | null | undefined
    students?: Array<number>
    params?:
      | {
          standardId?: RlStandardIdT
          skillId?: RlSkillUspIdT
          supportSetting?: SupportSettingT
          subType?: AssignTargetedMathSubtypeT
          share?: boolean
        }
      | null
      | undefined
  }) => {
    const baseQueryParams = {students, numQuestions}
    const additionalQueryParams =
      params !== null && params !== undefined
        ? {
            subType: params.subType,
            ...(params.supportSetting !== undefined ? {supportSetting: params.supportSetting} : {}),
            standardId: params.standardId,
            skillId: params.skillId,
            share: params.share
          }
        : {}
    return toFragment(`/assignments/targeted`, {
      courseId,
      ...baseQueryParams,
      ...additionalQueryParams
    })
  },
  lessons: (courseId?: number) => toFragment(`/lessons`, {courseId}),
  lesson: ({courseId, lessonId}: {courseId?: number; lessonId: number}) =>
    toFragment(`/lessons/${lessonId}`, {courseId}),
  lessonDaySlideshow: (courseId: number, lessonId: number, dayNum: number, lang: LangT) =>
    toFragment(`/lessons/${lessonId}/days/${dayNum}/slideshow?language=${lang}`, {
      courseId
    }),
  printables: (params?: {courseId?: number; domainId?: RlDomainIdT; standardId?: RlStandardIdT}) =>
    toFragment(`/printables`, params),
  printableAssignments: {
    // This also accepts a special `includeQuestion` parameter for testing
    // specific questions (give it a math question ID)
    example: (courseId: number) => toFragment(`/printables/example`, {courseId}),
    adaptive: (courseId: number, domainId: RlDomainIdT) =>
      toFragment(`/printables/adaptive/domain/${domainId}`, {courseId}),
    targeted: ({
      courseId,
      skillId,
      subSkillId
    }: {
      courseId?: number
      skillId: RlSkillUspIdT
      subSkillId?: RlSkillUspIdT
    }) => toFragment(`/printables/targeted/skill/${skillId}`, {courseId, subSkillId})
  },
  constructedResponses: (params?: {
    courseId?: number
    domainId?: RlDomainIdT
    standardCode?: string
  }) => toFragment(`/constructed-responses`, params)
}

const elaRoutes = {
  elaAssignmentReports: (assignmentType: ElaAssignmentReport, assignmentId: number) =>
    `/ela/reports/${assignmentType}/${assignmentId}`,
  elaReportsAssessmentSessions: ({courseId, sessionId}: {courseId?: number; sessionId: number}) =>
    toFragment(`/ela/reports/assessment-sessions/${sessionId}`, {courseId}),
  overview: (params?: {courseId?: number; grade?: number; tab?: string}) =>
    toFragment(`/ela/overview`, {tab: 'standards', ...params}),
  defaultCourseArticle: (articleUuid: string) => `/default-course/ela/articles/${articleUuid}`,
  article: (params: {courseId?: number; articleUuid: string}) =>
    toFragment(`/ela/articles/${params.articleUuid}`, {courseId: params.courseId}),
  articles: (params?: {courseId?: number; standardId?: string}) =>
    toFragment('/ela/articles', params),
  standard: (domainId: RlDomainIdT, standardId: RlStandardIdT) =>
    `/ela/standards/${domainId}/${standardId}`,
  assignmentReadingStudentReports: ({
    courseId,
    assignmentId,
    studentId
  }: {
    courseId?: number
    assignmentId: number
    studentId: number
  }) => toFragment(`/ela/reports/reading/${assignmentId}/students/${studentId}`, {courseId}),
  assignmentWritingReports: (assignmentId: number) => `/ela/reports/writing/${assignmentId}`,
  assignmentTargetedStudentReport: ({
    courseId,
    assignmentId,
    studentId
  }: {
    courseId?: number
    assignmentId: number
    studentId: number
  }) =>
    toFragment(`/ela/reports/targeted-skills/${assignmentId}/students/${studentId}`, {courseId}),
  chromelessAssignmentTargetedStudentReport: ({
    assignmentId,
    studentId
  }: {
    assignmentId: number
    studentId: number
  }) => `/chromeless/ela/reports/targeted-skills/${assignmentId}/students/${studentId}`,
  assignmentFocusSkillsStudentReport: ({
    courseId,
    assignmentId,
    studentId
  }: {
    courseId?: number
    assignmentId: number
    studentId: number
  }) => toFragment(`/ela/reports/focus-skills/${assignmentId}/students/${studentId}`, {courseId}),
  assignmentAdaptiveSkillStudentReport: ({
    courseId,
    assignmentId,
    studentId
  }: {
    courseId?: number
    assignmentId: number
    studentId: number
  }) =>
    toFragment(`/ela/reports/adaptive-skills/${assignmentId}/students/${studentId}`, {
      courseId
    }),
  assignmentAdaptiveGrammarStudentReport: (
    courseId: number,
    assignmentId: number,
    studentId: number
  ) =>
    toFragment(`/ela/reports/adaptive-grammar/${assignmentId}/students/${studentId}`, {
      courseId
    }),
  assignmentTargetedGrammarStudentReport: (
    courseId: number,
    assignmentId: number,
    studentId: number
  ) =>
    toFragment(`/ela/reports/targeted-grammar/${assignmentId}/students/${studentId}`, {
      courseId
    }),
  wordStudyAssignmentReport: (assignmentId: number) => `/ela/reports/word-study/${assignmentId}`,
  chromelessWordStudyAssignmentReport: (assignmentId: number) =>
    `/chromeless/ela/reports/word-study/${assignmentId}`,
  assignmentWordStudyStudentReport: ({
    courseId,
    sessionId,
    studentId
  }: {
    courseId?: number
    sessionId: number
    studentId: number
  }) =>
    toFragment(`/ela/reports/word-study/${sessionId}/students/${studentId}`, {
      courseId
    }),
  chromelessAssignmentWordStudyStudentReport: ({
    courseId,
    sessionId,
    studentId
  }: {
    courseId?: number
    sessionId: number
    studentId: number
  }) =>
    toFragment(`/chromeless/ela/reports/word-study/${sessionId}/students/${studentId}`, {
      courseId
    }),
  targetedSkillsAssignmentReport: ({
    courseId,
    assignmentId
  }: {
    courseId?: number
    assignmentId: number
  }) => toFragment(`/ela/reports/targeted-skills-assignment/${assignmentId}`, {courseId}),
  chromelessTargetedSkillsAssignmentReport: (assignmentId: number) =>
    `/chromeless/ela/reports/targeted-skills-assignment/${assignmentId}`,
  textLevelCorrelationChart: () => `/ela/correlation`,
  standardsStudentReport: (
    studentId: number,
    params?: {
      from?: number
      to?: number
      'date-option'?: DateOption
      standardId?: RlStandardIdT
      domainId?: string
      practiceType?: ElaAssignmentOptionT
    }
  ) => toFragment(`/ela/standards/students/${studentId}/report`, {...params}),
  previewPrintTargetedSkillsPractice: (courseId: number, sessionId: number) =>
    toFragment(`/ela/print/targeted-skills/${sessionId}`, {courseId}),
  previewPrintFocusSkillsPractice: (courseId: number, assignmentId: number) =>
    toFragment(`/ela/print/focus-skills/${assignmentId}`, {courseId}),
  previewPrintAdaptiveSkillsPractice: (courseId: number, assignmentId: number) =>
    toFragment(`/ela/print/adaptive-skills/${assignmentId}`, {courseId}),
  previewPrintArticleFromSession: (courseId: number, sessionId: number) =>
    toFragment(`/ela/print/articles/${sessionId}`, {courseId}),
  previewPrintArticleFromUuidAndLevel: ({
    courseId,
    uuid,
    level
  }: {
    courseId?: number
    uuid: string
    level: string
  }) => toFragment(`/ela/print/article/${uuid}/level/${level}`, {courseId}),
  articleSessionHistory: (courseId?: number) =>
    toFragment(`/session-history/ela-article`, {courseId}),
  classActivityArticleDetail: ({courseId, articleUuid}: {courseId?: number; articleUuid: string}) =>
    toFragment(`/session-history/ela-article/${articleUuid}`, {courseId}),
  groupReport: (courseId?: number) => toFragment(`/ela/groups-report`, {courseId}),
  performanceByStandardReport: (
    courseId?: number,
    params?: {
      from?: number
      to?: number
      dateOption?: DateOption
    }
  ) => toFragment(`/ela/reports/performance-by-standard`, {courseId, ...params}),
  studentGoals: (courseId?: number) => toFragment(`/ela/student-goals`, {courseId}),
  reportsAssessments: (courseId?: number) => toFragment(`/ela/reports/assessments`, {courseId}),
  assessments: (params?: {courseId?: number; students?: Array<number>; assessmentUuid?: string}) =>
    toFragment(`/ela/assessments`, {...params}),
  wordStudy: (courseId?: number) => toFragment(`/ela/word-study`, {courseId}),
  assignTargetedPracticeSkill: (params?: {
    courseId?: number
    students?: Array<number>
    elaDomainId?: RlDomainIdT | null | undefined
    elaStandardId?: RlStandardIdT | null | undefined
    elaSkillUspId?: RlSkillUspIdT | null | undefined
  }) => toFragment(`/ela/targeted-skill-practice/assign`, {...params}),
  assignWordStudyPractice: ({
    courseId,
    studentIds,
    elaStandardId,
    elaSkillUspId
  }: {
    courseId?: number
    studentIds?: Array<number>
    elaStandardId?: RlStandardIdT | null | undefined
    elaSkillUspId?: RlSkillUspIdT | null | undefined
  }) =>
    toFragment(`/ela/word-study`, {
      courseId,
      studentIds,
      elaStandardId,
      elaSkillUspId
    }),
  assignAdaptiveWordStudyPractice: (params?: {courseId?: number; studentIds?: Array<number>}) => {
    return toFragment(`/ela/word-study`, {
      type: 'Adaptive',
      ...params
    })
  },
  assignDecodable: ({
    courseId,
    students,
    decodableUuid
  }: {
    courseId?: number
    students?: Array<number>
    decodableUuid?: string
  }) =>
    toFragment(`/ela/decodable/assign`, {
      courseId,
      students,
      decodableUuid
    }),
  assignFocusSkillsPractice: ({
    courseId,
    studentIds,
    share
  }: {
    courseId?: number
    studentIds?: Array<number>
    share?: boolean
  }) => toFragment(`/assignments/ela-focus-skills-practice`, {courseId, studentIds, share}),
  assignments: (courseId?: number) => toFragment(`/ela/assignments`, {courseId}),
  reportCards: (courseId?: number) => toFragment(`/ela/report-cards`, {courseId}),
  printElaReportCards: (courseId?: number) => toFragment(`/ela/print-ela-report-cards`, {courseId}),
  reportCard: (params?: {courseId?: number; studentId?: number; queryParams?: object}) => {
    return toFragment(`/ela/report-card`, {
      courseId: params?.courseId,
      studentId: params?.studentId,
      ...params?.queryParams
    })
  },
  wordStudyMatrix: (courseId?: number) => toFragment(`/ela/word-study-matrix`, {courseId}),
  assignArticle: ({
    courseId,
    articleUuid,
    students,
    type,
    gradeLevelType,
    elaArticleLevelId
  }: {
    courseId?: number
    articleUuid: string
    students?: Array<number>
    type?: ElaAssignmentTypeT
    gradeLevelType?: 'adaptive' | 'uniform' | 'custom'
    elaArticleLevelId?: string | undefined
  }) =>
    toFragment(`/ela/articles/${articleUuid}/assign`, {
      courseId,
      students,
      type,
      gradeLevelType,
      elaArticleLevelId
    }),
  levelsReport: (courseId?: number) => toFragment(`/ela/reports/levels`, {courseId}),
  skillsProgressReport: (courseId?: number) =>
    toFragment(`/ela-skills-progress-report`, {courseId}),

  focusSkillsPracticeReport: (courseId: number, assignmentId: number) =>
    toFragment(`/reports/ela-focus-skills-practice/${assignmentId}`, {
      courseId
    })
}

const scienceRoutes = {
  units: () => '/science-units',
  unit: (scienceUnitId: string, lesson?: number | null | undefined) =>
    toFragment(`/science-units/${scienceUnitId}`, {lesson}),
  unitArticle: (scienceUnitId: string, articleUuid: string) =>
    `/science-units/${scienceUnitId}/articles/${articleUuid}`,
  readingAssignmentSessionReport: (assignmentId: number, studentId: number) =>
    `/science/reports/reading/${assignmentId}/students/${studentId}`,
  writingAssignmentReport: (assignmentId: number) => `/science/reports/writing/${assignmentId}`,
  search: (query: string) => toFragment('/science/search', {q: query}),
  assignUnitArticle: ({
    courseId,
    scienceUnit,
    articleUuid,
    type
  }: {
    courseId?: number
    scienceUnit: string
    articleUuid: string
    type?: 'Reading' | 'Writing'
  }) =>
    toFragment(`/science-units/${scienceUnit}/articles/${articleUuid}/assign`, {
      courseId,
      type
    }),
  assignments: (courseId?: number) => toFragment(`/science/assignments`, {courseId}),
  assignmentSessionsHistory: () => '/reports-overview?tab=science'
}

const socialStudiesRoutes = {
  units: () => '/social-studies-units',
  unit: (socialStudiesUnitId: string, lesson?: number | null | undefined) =>
    toFragment(`/social-studies-units/${socialStudiesUnitId}`, {lesson}),
  unitArticle: (socialStudiesUnitId: string, articleUuid: string) =>
    `/social-studies-units/${socialStudiesUnitId}/articles/${articleUuid}`,
  readingAssignmentSessionReport: (assignmentId: number, studentId: number) =>
    `/social-studies/reports/reading/${assignmentId}/students/${studentId}`,
  writingAssignmentReport: (assignmentId: number) =>
    `/social-studies/reports/writing/${assignmentId}`,
  search: (query: string) => toFragment('/social-studies/search', {q: query}),
  assignUnitArticle: ({
    courseId,
    socialStudiesUnit,
    articleUuid,
    type
  }: {
    courseId?: number
    socialStudiesUnit: string
    articleUuid: string
    type?: 'Reading' | 'Writing'
  }) =>
    toFragment(`/social-studies-units/${socialStudiesUnit}/articles/${articleUuid}/assign`, {
      courseId,
      type
    }),
  assignments: (courseId?: number) => toFragment(`/social-studies/assignments`, {courseId}),
  assignmentSessionsHistory: () => '/reports-overview?tab=social_studies'
}

const authRoutes = {
  signup: () => '/signup',
  login: () => '/login',
  resetByEmail: () => '/reset-by-email'
}

const rosteringRoutes = {
  rosters: (expandedCourses?: number[]) => toFragment('/rosters', {expandedCourses}),
  create: () => '/rosters/create',
  editClass: (courseId?: number) => toFragment(`/rosters/edit`, {courseId}),
  archivedRoster: () => '/rosters/archived-roster',
  loginCards: () => '/rosters/login-cards',
  loginCardsForCourse: (courseId?: number) => toFragment('/rosters/login-cards', {courseId}),
  loginCardForStudent: (
    studentId: number,
    courseId: number,
    displayPassword?: boolean | null | undefined
  ) => toFragment('/rosters/login-cards', {studentId, courseId, displayPassword}),
  rostersCoursesStudentEdit: (
    courseId: number,
    studentId: number,
    tab?: RosterStudentEditPageTab
  ) => toFragment(`/students/${studentId}/edit`, {courseId, tab})
}

export const Routes = {
  ...authRoutes,

  profileSettings: () => '/settings/profile',
  productSettings: () => '/settings/product',
  purchasePremiumLicenseSuccess: () => '/settings/profile?premium-activated',
  assignments: () => '/assignments',
  assignmentsWithShareError: () => '/assignments?share-error',
  ssoLogoutToRenaissance: () => `${PATHS.unversionedAPIUrl}/3/renaissance/home`,
  mobileReminder: () => '/mobile-reminder',

  dashboard: (showOnboardingWalkthrough?: boolean) =>
    toFragment('/dashboard', {showOnboardingWalkthrough}),
  chromelessError: (args: {fromPage: string; errorMessage?: string | undefined}) =>
    toFragment('/chromeless/error', args),

  pricing: () => '/pricing',
  checkout: () => '/checkout',

  referralProgram: () => '/referral-program',

  premiumReferralProgram: () => '/premium-referral-program',

  teacherOnboarding: () => '/teacher-onboarding',
  parentWelcome: () => '/parent-welcome',

  activityFeed: (courseId?: number) => toFragment(`/activity-feed`, {courseId}),
  studentGoals: (courseId?: number) => toFragment(`/student-goals`, {courseId}),
  reportsOverview: (tab?: SubjectT) => toFragment(`/reports-overview`, {tab}),

  rostering: rosteringRoutes,

  math: mathRoutes,

  ela: elaRoutes,

  socialStudies: socialStudiesRoutes,

  science: scienceRoutes
}
